// AuthContext - session state for the Profile Manager module.
// Rebuilt to match the profilemgr backend (OTP login -> JWT stored in localStorage 'token').
import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Several services (e.g. bulkopsadmin/AdminService) call the API without an
// Authorization header and rely on it being attached globally.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.url?.startsWith(API_URL) && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = createContext({
  currentUser: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => null,
  logout: async () => {},
  refreshUser: async () => null,
  checkTrialStatus: () => null,
});

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// /api/profile returns the profile (with profileId, role and trial fields) for the token's user
const fetchProfile = async (token) => {
  const { data } = await axios.get(`${API_URL}/api/profile`, authHeaders(token));
  return data;
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userData');
  localStorage.removeItem('trialInfo');
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const storeUser = useCallback((user) => {
    localStorage.setItem('userData', JSON.stringify(user));
    setCurrentUser(user);
    return user;
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const profile = await fetchProfile(token);
    const cached = JSON.parse(localStorage.getItem('userData') || '{}');
    return storeUser({ ...cached, ...profile, profileId: profile.profileId || profile.id });
  }, [storeUser]);

  // Restore the session on page load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    refreshUser()
      .catch((err) => {
        if ([401, 403, 404].includes(err.response?.status)) {
          clearSession();
          setCurrentUser(null);
        } else {
          // Backend unreachable: keep the cached user rather than logging out
          const cached = localStorage.getItem('userData');
          if (cached) setCurrentUser(JSON.parse(cached));
        }
      })
      .finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (token, userData = {}) => {
    localStorage.setItem('token', token);
    let user = { ...userData };
    try {
      const profile = await fetchProfile(token);
      user = { ...user, ...profile, profileId: profile.profileId || profile.id };
    } catch (err) {
      console.error('Could not load profile after login:', err);
      user.role = user.role || 'user';
    }
    return storeUser(user);
  }, [storeUser]);

  const logout = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await axios.post(`${API_URL}/api/auth/logout`, {}, authHeaders(token)).catch(() => {});
    }
    clearSession();
    setCurrentUser(null);
  }, []);

  const checkTrialStatus = useCallback(() => {
    if (!currentUser?.is_trial) return { isTrial: false };
    const trialEndDate = currentUser.trial_end_date ? new Date(currentUser.trial_end_date) : null;
    const msLeft = trialEndDate ? trialEndDate.getTime() - Date.now() : 0;
    return {
      isTrial: true,
      trialStartDate: currentUser.trial_start_date || null,
      trialEndDate: currentUser.trial_end_date || null,
      daysRemaining: Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000))),
      isExpired: msLeft <= 0,
    };
  }, [currentUser]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated: !!currentUser,
        isLoading,
        login,
        logout,
        refreshUser,
        checkTrialStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
